import { useQuery } from '@tanstack/react-query'
import { type FC, useCallback, useMemo } from 'react'

import { SELECT_FILTER_OPTION_VALUE_MAP, type SelectFilterOptionValue } from '@/components/select-filter/constants'
import { SelectFilter } from '@/components/select-filter/select-filter'

import { CASE_ARCHIVE_STATUS_MAP } from '@/lib/queries/business/constants'
import { getCaseFiltersQuery } from '@/lib/queries/business/v2/case/filters/queries'
import { cn } from '@/lib/utils'

import { useCasesTableContext } from '../../contexts'
import { CasesKeywordFilter } from './keyword-filter/keyword-filter'
import { ResetCasesFilter } from './reset-filter/reset-filter'

export type CasesFilterChangeHandler = (params: {
  keyword: string
  tags: string[]
  processingTimes: string[]
  statuses: string[]
  tagFilterOption?: SelectFilterOptionValue
  processingTimeFilterOption?: SelectFilterOptionValue
}) => void

export interface CasesFilterProps {
  keyword?: string
  tagFilterOption?: SelectFilterOptionValue
  tags?: string[]
  processingTimeFilterOption?: SelectFilterOptionValue
  processingTimes?: string[]
  statuses?: string[]
  disabled?: boolean
  onChange?: CasesFilterChangeHandler
}

export const CasesFilter: FC<CasesFilterProps> = ({
  keyword = '',
  tagFilterOption = SELECT_FILTER_OPTION_VALUE_MAP.or,
  tags = [],
  processingTimeFilterOption = SELECT_FILTER_OPTION_VALUE_MAP.or,
  processingTimes = [],
  statuses = [],
  disabled = false,
  onChange,
}) => {
  const { tableType } = useCasesTableContext()

  const { data: dataFilters } = useQuery(
    getCaseFiltersQuery({
      archiveStatus: tableType === 'active' ? CASE_ARCHIVE_STATUS_MAP.ACTIVE : CASE_ARCHIVE_STATUS_MAP.ARCHIVED,
    })
  )

  const currentFilterParams = useMemo(
    () => ({
      keyword,
      tags,
      processingTimes,
      statuses,
      tagFilterOption,
      processingTimeFilterOption,
    }),
    [keyword, tags, processingTimes, statuses, tagFilterOption, processingTimeFilterOption]
  )

  const isFilterApplied = useMemo(
    () => !!keyword || tags.length > 0 || processingTimes.length > 0 || statuses.length > 0,
    [keyword, tags, processingTimes, statuses]
  )

  const handleChange: CasesFilterChangeHandler = useCallback(
    (params) => {
      onChange?.(params)
    },
    [onChange]
  )

  const handleKeywordChange = useCallback(
    (keyword: string) => {
      handleChange?.({ ...currentFilterParams, keyword })
    },
    [handleChange, currentFilterParams]
  )

  const handleStatusesChange = useCallback(
    (statuses: string[]) => {
      onChange?.({ ...currentFilterParams, statuses })
    },
    [onChange, currentFilterParams]
  )

  const handleTagsChange = useCallback(
    (tags: string[]) => {
      handleChange?.({
        ...currentFilterParams,
        tags,
      })
    },
    [handleChange, currentFilterParams]
  )

  const handleProcessingTimesChange = useCallback(
    (processingTimes: string[]) => {
      handleChange?.({ ...currentFilterParams, processingTimes })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeTagFilterSelectOption = useCallback(
    (option: SelectFilterOptionValue) => {
      handleChange?.({ ...currentFilterParams, tagFilterOption: option })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeProcessingTimesFilterSelectOption = useCallback(
    (option: SelectFilterOptionValue) => {
      handleChange?.({
        ...currentFilterParams,
        processingTimeFilterOption: option,
      })
    },
    [handleChange, currentFilterParams]
  )

  const handleReset = useCallback(() => {
    handleChange?.({
      keyword: '',
      statuses: [],
      tags: [],
      processingTimes: [],
      tagFilterOption: undefined,
      processingTimeFilterOption: undefined,
    })
  }, [handleChange])

  return (
    <div className={cn('flex flex-wrap items-center gap-2')}>
      <CasesKeywordFilter disabled={disabled} keyword={keyword} onChange={handleKeywordChange} />
      <SelectFilter
        disabled={disabled}
        buttonText="Status"
        value={statuses}
        items={dataFilters?.status ?? []}
        onChangeValue={handleStatusesChange}
      />
      <SelectFilter
        disabled={disabled}
        buttonText="Tag"
        value={tags}
        items={dataFilters?.tags ?? []}
        showsFilterOption
        filterOption={tagFilterOption}
        onChangeValue={handleTagsChange}
        onChangeFilterOption={handleChangeTagFilterSelectOption}
      />
      <SelectFilter
        disabled={disabled}
        buttonText="Processing Time"
        value={processingTimes}
        items={dataFilters?.processingTime ?? []}
        showsFilterOption
        filterOption={processingTimeFilterOption}
        onChangeValue={handleProcessingTimesChange}
        onChangeFilterOption={handleChangeProcessingTimesFilterSelectOption}
      />
      {isFilterApplied && <ResetCasesFilter onClick={handleReset} />}
    </div>
  )
}
